import React, {useContext, useReducer, useRef, useState} from 'react'
import Tr from 'translations/Translations';
import Dialog from 'components/Dialog';
import PropTypes from 'prop-types'
import {useParams} from "react-router";
import FormTranslationContext from "context/FormTranslationContext";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import AnnualReportsService from "services/AnnualReportsService";
import useSnackbar from "hooks/useSnackbar";
import {annualReportStepsOrder, annualReportTablesWithInitialize} from "../../constants/annualReportSteps";
import iconWorksReducer from "libs/iconWorksReducer";
import TypographyWithIcon from "components/TypographyWithIcon";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import useDebouncedEffect from "hooks/useDebouncedEffect";
import annualReportValidation from "../../constants/annualReportValidation";
import Form from "components/Form";
import ReplayIcon from "@material-ui/icons/Replay";
import {simpleConfirmNoTranslate} from "libs/ConfirmNotification";
import WorkableButton from "components/button/WorkableButton";
import isResponseValid from "libs/isResponseValid";
import scrollToContainer from "libs/scrollToContainer";

export const getAnnualReportEditPopup = (entry, newEntry = false, autoValidate = false) => {
    if (newEntry) {
        entry = {identity: 0}
    }
    return ({entry, newEntry, autoValidate})
}

function AnnualReportEditPopup(
    {
        onClose,
        config,
        reportStep,
        FormComponent
    }) {
    const [error, setError] = useState(null);
    const params = useParams();
    const {formTr} = useContext(FormTranslationContext)
    const form = useRef();
    const setForm = (formikProps) => form.current = formikProps;
    const submitSemaphore = useRef(false);

    const [buttonWorks, dispatchButtonWorks] = useReducer(iconWorksReducer, {
        anyWorks: false,
        save: false,
        draft: false,
        reload: false
    });
    const handleSubmit = (complete) => {
        form.current.setFieldValue('complete', complete);
        submitSemaphore.current = true;
        setTimeout(() => form.current.submitForm(), 0);
        setTimeout((formId, submitSemaphore) => submitSemaphore.current && scrollToContainer("#annual-report-edit-popup .MuiDialogContent-root", "#" + formId + " .MuiFormHelperText-root.Mui-error", 200), 200, reportStep, submitSemaphore);

    }
    const {enqueueSuccessSnackbar, enqueueErrorSnackbar} = useSnackbar();

    useDebouncedEffect(() => {
        if (config.autoValidate)
            handleSubmit(true);
    }, {timeout: 500, ignoreInitialCall: false}, [])

    const submitFrom = (values, actions) => {
        submitSemaphore.current = false;
        setError(null);
        dispatchButtonWorks({type: values.complete ? "save" : "draft", works: true});
        AnnualReportsService.saveAnnualReportTableEntry(params.id, reportStep, values).then((resp) => {
            if (resp) {
                if (resp.success) {
                    enqueueSuccessSnackbar(Tr("saved") + " " + resp.result.number);
                    onClose();
                } else {
                    dispatchButtonWorks({type: values.complete ? "save" : "draft", works: false})
                    if (resp.errors instanceof Object) {
                        for (let key of Object.keys(resp.errors)) {
                            setError(formTr(`mmia.${reportStep}.${key}`) + ": " + resp.errors[key])
                            break;
                        }
                    }

                }
            } else {
                dispatchButtonWorks({type: values.complete ? "save" : "draft", works: false})
            }
        }).catch(() => dispatchButtonWorks({type: values.complete ? "save" : "draft", works: false}))

    }

    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr('cancel'), onClose, 'outlined', buttonWorks.anyWorks));
        buttons.push(createButton(Tr('save_draft_version'), () => handleSubmit(false), 'outlined', buttonWorks.anyWorks, undefined, buttonWorks.draft));
        buttons.push(createButton(Tr('save'), () => handleSubmit(true), undefined, buttonWorks.anyWorks, undefined, buttonWorks.save));
        return buttons;
    }

    const getReloadButton = () => {
        return annualReportTablesWithInitialize.includes(reportStep) && !config.newEntry &&
            <WorkableButton working={buttonWorks.reload} disabled={buttonWorks.anyWorks} Icon={ReplayIcon} variant="outlined"
                            onClick={() => simpleConfirmNoTranslate(Tr("annual_report_reload_demand_confirm"),
                                () => {
                                    dispatchButtonWorks({type: "reload", works: true})
                                    AnnualReportsService.initializeTableEntry(params.id, reportStep, config.entry.type, config.entry.identity).then(resp => {
                                        if (isResponseValid(resp, enqueueErrorSnackbar)) {
                                            config.entry = resp.result;
                                            form.current.resetForm();
                                            enqueueSuccessSnackbar(Tr("annual_report_reloaded_demand"))
                                        }
                                    }).finally(() => dispatchButtonWorks({type: "reload", works: false}))
                                }
                            )}
            >{Tr("annual_report_reload_demand")}</WorkableButton>
    }

    return (
        <Dialog
            open={true}
            title={!!error ? <TypographyWithIcon Icon={ErrorOutlineIcon} color='error'>{error}</TypographyWithIcon> : null}
            maxWidth="md"
            dialogActions={
                <MultiButton
                    before={getReloadButton()}
                    buttons={getButtons()}
                />
            }
            htmlId="annual-report-edit-popup"
        >
            <Form
                htmlFormId={reportStep}
                initialValues={{
                    ...config.entry
                }}
                validationSchema={annualReportValidation[reportStep]}
                onSubmit={submitFrom}
            >
                {(formikProps) => {
                    setForm(formikProps);
                    return (<FormComponent
                        entry={config.entry}
                        newEntry={config.newEntry}
                        buttonWorks={buttonWorks}
                        reportStep={reportStep}
                        formikProps={formikProps}
                    />)
                }}
            </Form>

        </Dialog>
    )
}

AnnualReportEditPopup.propTypes = {
    onClose: PropTypes.func.isRequired,
    config: PropTypes.shape({
        entry: PropTypes.object,
        newEntry: PropTypes.bool,
        autoValidate: PropTypes.bool
    }),
    reportStep: PropTypes.oneOf(annualReportStepsOrder).isRequired,
    FormComponent: PropTypes.func.isRequired
}

export default AnnualReportEditPopup;